import envs from '../../common/envs';

export interface AthenaConfig {
  baseUrl: string;
  apiKey: string;
  practiceId: string;
}

export class AthenaClient {
  public config: AthenaConfig;

  constructor(config: AthenaConfig) {
    this.config = config;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // CSP 문제 해결을 위해 백엔드 API를 통해 프록시
    const url = `${this.config.baseUrl}/api/athena${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: options.signal, // AbortSignal 지원
    });

    if (!response.ok) {
      throw new Error(`Athena API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Use Case #1: Patient Check-in & Appointment Management
  async checkInPatient(appointmentId: string): Promise<any> {
    return this.request(`/appointments/${appointmentId}/checkin`, {
      method: 'POST',
    });
  }

  async getAppointment(appointmentId: string): Promise<any> {
    return this.request(`/appointments/${appointmentId}`);
  }

  // Get appointments by patient ID with optional date filter
  async getAppointmentsByPatient(patientId: string, date?: string): Promise<any[]> {
    let endpoint = `/appointments?patientid=${patientId}`;
    if (date) {
      endpoint += `&startdate=${date}&enddate=${date}`;
    }
    return this.request(endpoint);
  }

  // Get appointment by encounter ID
  async getAppointmentByEncounter(encounterId: string): Promise<any> {
    return this.request(`/chart/encounter/${encounterId}/appointment`);
  }

  async createAppointment(appointmentData: any): Promise<any> {
    return this.request(`/appointments`, {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async updateAppointment(appointmentId: string, appointmentData: any): Promise<any> {
    return this.request(`/appointments/${appointmentId}`, {
      method: 'PUT',
      body: JSON.stringify(appointmentData),
    });
  }

  async getAppointmentType(appointmentTypeId: string): Promise<any> {
    return this.request(`/appointmenttypes/${appointmentTypeId}`);
  }

  // Book Appointment Slots within athenaOne 플로우

  // 1. Get a list of departments
  async getDepartments(): Promise<any> {
    return this.request(`/departments`);
  }

  // 2. Get a list of providers
  async getProviders(): Promise<any> {
    return this.request(`/providers`);
  }

  // 3. Get patient appointment reasons
  async getPatientAppointmentReasons(reasonType?: string): Promise<any> {
    let endpoint = `/patient-appointment-reasons`;
    if (reasonType) {
      endpoint += `?reasonType=${reasonType}`;
    }
    return this.request(endpoint);
  }

  // 4. Get open appointments
  async getOpenAppointments(params: {
    reasonid?: string;
    departmentid?: string;
    providerid?: string;
    startdate?: string;
    enddate?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) queryParams.append(key, value);
    });
    
    const endpoint = `/open-appointments?${queryParams.toString()}`;
    return this.request(endpoint);
  }

  // 5a. Create a new patient
  async createPatient(patientData: {
    firstname: string;
    lastname: string;
    dob: string;
    departmentid: string;
    homephone?: string;
    mobilephone?: string;
    workphone?: string;
  }): Promise<any> {
    console.log('athenaClient.createPatient called with:', patientData);
    const result = await this.request(`/patients`, {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
    console.log('athenaClient.createPatient result:', result);
    return result;
  }

  // 5b. Find existing patient
  async findPatient(searchParams: {
    firstname: string;
    lastname: string;
    dob: string;
    departmentid: string;
    homephone?: string;
    mobilephone?: string;
    workphone?: string;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value) queryParams.append(key, value);
    });
    
    const endpoint = `/patients?${queryParams.toString()}`;
    return this.request(endpoint);
  }

  // 6. Book a patient appointment
  async bookPatientAppointment(appointmentId: string, bookingData: {
    patientid: string;
    reasonid: string;
    appointmentid: string;
  }): Promise<any> {
    return this.request(`/appointments/${appointmentId}/book`, {
      method: 'PUT',
      body: JSON.stringify(bookingData),
    });
  }

  // Use Case #2: HPI (History of Present Illness) - HPI Findings API
  async getHPI(encounterId: string): Promise<any> {
    return this.request(`/chart/encounter/${encounterId}/hpi`);
  }

  async createHPI(encounterId: string, hpiData: any): Promise<any> {
    return this.request(`/chart/encounter/${encounterId}/hpi`, {
      method: 'POST',
      body: JSON.stringify(hpiData),
    });
  }

  async updateHPI(encounterId: string, hpiData: any): Promise<any> {
    return this.request(`/chart/encounter/${encounterId}/hpi`, {
      method: 'PUT',
      body: JSON.stringify(hpiData),
    });
  }

  // Use Case #3: Health History Forms
  async getHealthHistoryForms(appointmentId: string): Promise<any> {
    return this.request(`/appointments/${appointmentId}/healthhistoryforms`);
  }

  async createHealthHistoryForm(appointmentId: string, formData: any): Promise<any> {
    return this.request(`/appointments/${appointmentId}/healthhistoryforms`, {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  async updateHealthHistoryForm(
    appointmentId: string,
    formId: string,
    formData: any
  ): Promise<any> {
    return this.request(`/appointments/${appointmentId}/healthhistoryforms/${formId}`, {
      method: 'PUT',
      body: JSON.stringify(formData),
    });
  }

  // Pharmacy Information - Default Pharmacy API
  async getDefaultPharmacy(patientId: string): Promise<any> {
    return this.request(`/chart/${patientId}/pharmacies/default`);
  }

  async updateDefaultPharmacy(patientId: string, pharmacyData: any): Promise<any> {
    return this.request(`/chart/${patientId}/pharmacies/default`, {
      method: 'PUT',
      body: JSON.stringify(pharmacyData),
    });
  }

  // Pharmacy Information - Preferred Pharmacy API
  async getPreferredPharmacies(patientId: string): Promise<any> {
    return this.request(`/chart/${patientId}/pharmacies/preferred`);
  }

  async addPreferredPharmacy(patientId: string, pharmacyData: any): Promise<any> {
    return this.request(`/chart/${patientId}/pharmacies/preferred`, {
      method: 'POST',
      body: JSON.stringify(pharmacyData),
    });
  }

  async updatePreferredPharmacy(patientId: string, pharmacyData: any): Promise<any> {
    return this.request(`/chart/${patientId}/pharmacies/preferred`, {
      method: 'PUT',
      body: JSON.stringify(pharmacyData),
    });
  }

  async getPharmacyOrganizations(): Promise<any> {
    return this.request('/fhir/r4/Organization?type=pharmacy');
  }

  // Care Team (PCP) Information
  async getCareTeam(patientId: string): Promise<any> {
    return this.request(`/chart/${patientId}/careteam`);
  }

  async updateCareTeam(patientId: string, careTeamData: any): Promise<any> {
    return this.request(`/chart/${patientId}/careteam`, {
      method: 'PUT',
      body: JSON.stringify(careTeamData),
    });
  }

  // 8. Put Section Note
  async putSectionNote(patientId: string, departmentId: string, note: string): Promise<any> {
    return this.request(`/patients/${patientId}/section-note`, {
      method: 'PUT',
      body: JSON.stringify({ note, departmentId, patientId }),
    });
  }

  async getEncounter(patientId: string, departmentId: string): Promise<any> {
    return this.request(`/patients/${patientId}/${departmentId}/encounter`, {
      method: 'GET',
    });
  }

  async getEncounterById(encounterId: string): Promise<any> {
    return this.request(`/encounters/${encounterId}`, {
      method: 'GET',
    });
  }

  async putHPI(encounterId: string, hpiData: any): Promise<any> {
    return this.request(`/encounters/${encounterId}/hpi`, {
      method: 'POST',
      body: JSON.stringify(hpiData),
    });
  }

  async updateHpi(encounterId: string, hpiData: any): Promise<any> {
    return this.request(`/encounters/${encounterId}/hpi`, {
      method: 'PUT',
      body: JSON.stringify(hpiData),
    });
  }

  async getPatientDetails(patientId: string): Promise<any> {
    return this.request(`/patients/${patientId}`);
  }

  async getHealthHistoryForm(appointmentId: string): Promise<any> {
    return this.request(`/appointments/${appointmentId}/healthhistoryforms`);
  }

  async checkInfo(aid: string, lastName: string, birthDate: string): Promise<any> {
    return this.request(`/info/check`, {
      method: 'POST',
      body: JSON.stringify({ aid, lastName, birthDate }),
    });
  }

  async getCCDA(patientId: string, departmentId: string): Promise<any> {
    return this.request(`/patients/${patientId}/ccda?departmentid=${departmentId}`, {
      method: 'GET',
    });
  }

  async updateCCDA(patientId: string, ccda: string, departmentId: string): Promise<any> {
    return this.request(`/patients/${patientId}/ccda`, {
      method: 'PUT',
      body: JSON.stringify({ ccda, departmentId }),
    });
  }

  async getMedication(medication: string): Promise<any> {
    return this.request(`/search/medication?medication=${medication}`, {
      method: 'GET',
    });
  }

  async getAllergy(allergy: string): Promise<any> {
    return this.request(`/search/allergy?allergy=${allergy}`, {
      method: 'GET',
    });
  }

  async getReaction(): Promise<any> {
    return this.request(`/search/allergies/reactions`, {
      method: 'GET',
    });
  }

  async putAllergy(patientId: string, departmentId: string, allergies: any[], reactions: any[]): Promise<any> {
    return this.request(`/patients/${patientId}/allergies`, {
      method: 'PUT',
      body: JSON.stringify({ allergies, departmentId, reactions }),
    });
  }
  
  async postMedication(patientId: string, departmentId: string, medicationId: string): Promise<any> {
    return this.request(`/patients/${patientId}/medications`, {
      method: 'POST',
      body: JSON.stringify({ departmentId, medicationId }),
    });
  }

  async searchProvider(zip: string, name: string, orderType: string, signal?: AbortSignal): Promise<any> {
    let url = '';
    if (orderType === 'CONSULT') {
      url = `/search/provider?orderType=CONSULT&zip=${zip}&name=${encodeURIComponent(name)}`;
    } else if (orderType === 'PRESCRIPTION') {
      url = `/search/provider?orderType=PRESCRIPTION&zip=${zip}&name=${encodeURIComponent(name)}`;
    } else {
      url = `/search/provider?orderType=CONSULT&zip=${zip}&name=${encodeURIComponent(name)}`;
    }
    return this.request(url, {
      method: 'GET',
      signal, // AbortSignal 전달
    });
  }

  async putPharmacy(patientId: string, departmentId: string, pharmacyId: string): Promise<any> {
    return this.request(`/patient/${patientId}/pharmacies`, {
      method: 'PUT',
      body: JSON.stringify({ departmentId, pharmacyId }),
    });
  }

  async putProvider(patientId: string, departmentId: string, providerId: string): Promise<any> {
    return this.request(`/patient/${patientId}/providers`, {
      method: 'PUT',
      body: JSON.stringify({ departmentId, providerId }),
    });
  }

  async findExistProvider(patientId: string, departmentId: string): Promise<any> {
    return this.request(`/patients/find/${patientId}/provider?departmentId=${departmentId}`, {
      method: 'GET',
    });
  }
  
  async findExistPharmacy(patientId: string, departmentId: string): Promise<any> {
    return this.request(`/patients/find/${patientId}/pharmacy?departmentId=${departmentId}`, {
      method: 'GET',
    });
  }

  async findExistMedication(patientId: string, departmentId: string): Promise<any> {
    return this.request(`/patients/find/${patientId}/medication?departmentId=${departmentId}`, {
      method: 'GET',
    });
  }

  async checkEncounterStatus(encounterId: string): Promise<any> {
    return this.request(`/encounters/${encounterId}/status`, {
      method: 'GET',
    });
  }

  async findExistAllergy(patientId: string, departmentId: string): Promise<any> {
    return this.request(`/patients/find/${patientId}/allergy?departmentId=${departmentId}`, {
      method: 'GET',
    });
  }

  async handleHpiUpdate(encounterId: string, prompt: string, additionalText: string, visitId?: string): Promise<any> {
    return this.request(`/encounters/${encounterId}/hpi/update`, {
      method: 'POST',
      body: JSON.stringify({ prompt, additionalText, visitId }),
    });
  }

  async getCCDAExport(patientId: string, departmentId: string, encounterId: string): Promise<any> {
    return this.request(`/patients/${patientId}/${departmentId}/${encounterId}/ccdaexport`, {
      method: 'GET',
    });
  }

  async getHPIForUpdate(encounterId: string): Promise<any> {
    return this.request(`/encounters/${encounterId}/hpi/update`, {
      method: 'GET',
    });
  }

  async createFhirSubscription(deploymentId: string): Promise<any> {
    return this.request(`/subscription`, {
      method: 'POST',
      body: JSON.stringify({ deploymentId }),
    });
  }

  async deleteFhirSubscription(deploymentId: string): Promise<any> {
    return this.request(`/subscription/${deploymentId}`, {  
      method: 'POST',
    });
  }

  async checkSurveyStatusByAid(aid: string): Promise<any> {
    return this.request(`/survey-status/${aid}`, {
      method: 'GET',
    });
  }
}

// Singleton instance - 백엔드 API를 통해 프록시
export const athenaClient = new AthenaClient({
  baseUrl: envs.backendBaseUrl || 'https://api.cyrencare.com',
  apiKey: envs.athena.apiKey || '',
  practiceId: envs.athena.practiceId || '',
}); 